import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Loader2, PartyPopper, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const RsvpDialog = ({ open, onOpenChange, onSubmitted }) => {
  const [name, setName] = useState('');
  const [attending, setAttending] = useState('yes');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Please enter your name, operator!');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/rsvp`, {
        name: name.trim(),
        attending: attending === 'yes',
        message: message.trim(),
      });
      toast.success('RSVP saved. See you on site!', {
        description: attending === 'yes' ? "Advit can't wait to see you!" : 'Thanks for letting us know.',
      });
      setName('');
      setMessage('');
      setAttending('yes');
      onOpenChange(false);
      if (onSubmitted) onSubmitted();
    } catch (err) {
      toast.error('Could not save RSVP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]" data-testid="rsvp-dialog">
        <div className="absolute top-0 left-0 right-0 h-2 hazard-strip rounded-t-lg" />
        <DialogHeader className="pt-2">
          <DialogTitle className="font-display tracking-wide text-2xl flex items-center gap-2">
            <PartyPopper className="text-[hsl(var(--orange))]" size={22} /> RSVP for Advit's Big Dig
          </DialogTitle>
          <DialogDescription>Let us know if you can make it to Cottage 837.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-1">
          <div className="space-y-1.5">
            <Label htmlFor="rsvp-name">Your name</Label>
            <Input
              id="rsvp-name"
              data-testid="rsvp-name-input"
              placeholder="e.g. Site Operator Sam"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
            />
          </div>

          <div className="space-y-2">
            <Label>Can you make it?</Label>
            <RadioGroup
              value={attending}
              onValueChange={setAttending}
              className="grid grid-cols-2 gap-3"
              data-testid="rsvp-attending-radio"
            >
              <label
                htmlFor="att-yes"
                className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer ${
                  attending === 'yes' ? 'border-[hsl(var(--orange))] bg-orange-50' : 'border-black/10'
                }`}
              >
                <RadioGroupItem value="yes" id="att-yes" data-testid="rsvp-yes" />
                <span className="font-semibold">Yes, I'm in!</span>
              </label>
              <label
                htmlFor="att-no"
                className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer ${
                  attending === 'no' ? 'border-[hsl(var(--orange))] bg-orange-50' : 'border-black/10'
                }`}
              >
                <RadioGroupItem value="no" id="att-no" data-testid="rsvp-no" />
                <span className="font-semibold">Can't make it</span>
              </label>
            </RadioGroup>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="rsvp-msg">Message for Advit (optional)</Label>
            <Textarea
              id="rsvp-msg"
              data-testid="rsvp-message-textarea"
              placeholder="Happy 8th birthday, buddy!"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={500}
              rows={3}
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="rsvp-submit-button"
            className="w-full chunky-btn bg-[hsl(var(--orange))] hover:bg-[hsl(18_100%_50%)] text-white font-semibold py-6 text-base"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
            {loading ? 'Saving\u2026' : 'Send RSVP'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
